import { fetchFragmentArea } from '@gialaboratory/sdk/report-check-fragments';
import { FragmentRenderer, MobileDrawerProvider } from '@gialaboratory/sdk/fragment-renderer';
import { useQuery } from '@tanstack/react-query';
import React, { useContext } from 'react';
import { Link } from 'react-router';
import HeaderSkeleton from '@@components/Header/HeaderSkeleton';
import { TLocale } from '@@config/locale/';
import { LocaleContext, TLocaleContext } from '@@contexts/LocaleContext';
import { getAWSEnv, getMagnoliaRestBaseURL } from '@@utils/utils';

const FRAGMENT_PAGE_NAME = 'header' as const;

const fetchHeaderContent = async (locale: TLocale) => {
    const baseUrl = getMagnoliaRestBaseURL();
    try {
        const result = await fetchFragmentArea(FRAGMENT_PAGE_NAME, locale, { baseUrl });
        return result;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error(
            '[Header] Fragment fetch failed:',
            msg,
            'url:',
            `${baseUrl}/.rest/api/report-check/fragments/${FRAGMENT_PAGE_NAME}/${locale}`
        );
        throw err;
    }
};

const Header: React.FC = () => {
    const { locale }: TLocaleContext = useContext(LocaleContext);

    const {
        data: content,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['reportCheckFragment', FRAGMENT_PAGE_NAME, locale],
        queryFn: () => fetchHeaderContent(locale),
        staleTime: 15 * 60 * 1000, // 15 minutes
    });

    const renderLink = (href: string, children: React.ReactNode, className?: string) => {
        if (href?.includes('report-check-landing')) {
            return (
                <Link
                    className={className ?? 'nav-link'}
                    to='/report-check-landing'
                    reloadDocument={getAWSEnv() !== 'local'}
                >
                    {children || 'Report Check'}
                </Link>
            );
        }
        return (
            <a href={href} className={className}>
                {children}
            </a>
        );
    };

    if (isLoading) {
        return <HeaderSkeleton />;
    }

    if (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.error('[Header] Error loading header content:', msg);
        return (
            <>
                <header id='global-header' className='global-header' role='alert'>
                    <p>
                        Header content is not available. Ensure the report-check API is running at{' '}
                        {getMagnoliaRestBaseURL()}.
                    </p>
                </header>
                <hr className='solid-single' />
            </>
        );
    }

    const areaContent = content?.header;

    return (
        <>
            <header id='global-header' className='global-header'>
                {areaContent && (
                    <>
                        <FragmentRenderer
                            className='gia-fragment-content'
                            content={areaContent as Record<string, unknown>}
                            locale={locale}
                            magnoliaPublicBaseUrl={getMagnoliaRestBaseURL()}
                            wrapper={MobileDrawerProvider}
                            renderLink={renderLink}
                        />
                        <script async src='//cdn.userway.org/widget.js' />
                    </>
                )}
            </header>
            <hr className='solid-single' />
        </>
    );
};

export default Header;
